// ─── 질의 상세 모달 ───────────────────────────────────────────────────────────
import React, { useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FileText, Trash2 } from 'lucide-react';
import StatusBadge from '../shared/StatusBadge';
import { fmtTime, fmtMs, judge } from '../../utils/formatters';

/**
 * 선택된 질의의 상세 정보를 보여주는 모달.
 * @param {{ row: object, onClose: function, onDelete?: function }} props
 */
export default function DetailModal({ row, onClose, onDelete }) {
  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!row) return null;

  const j = judge(row.eval_score);

  return (
    <Modal show centered size="lg" onHide={onClose} contentClassName="rounded-2xl border-0 shadow-xl">
      <Modal.Header className="border-b border-gray-100 px-6 py-3">
        <Modal.Title className="flex items-center gap-2 text-base font-bold text-sb-header">
          <FileText size={16} className="text-gray-400" />
          질의 상세
          <span className="font-mono text-xs font-normal text-gray-400">#{row.turn_id}</span>
        </Modal.Title>
        <button type="button" className="modal-btn-close" onClick={onClose}>
          <span className="modal-btn-span">X</span>
        </button>
      </Modal.Header>

      <Modal.Body className="max-h-[70vh] overflow-y-auto px-6 py-3">
        {/* 메타 배지 영역 */}
        <div className="mb-4 flex flex-wrap gap-2 rounded-xl bg-gray-50 p-3">
          {[row.company_name, row.company_code, row.dept_name, row.user_name, row.user_id]
            .filter((x) => x && x !== '—')
            .map((t) => (
              <span key={t} className="rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-gray-600">
                {t}
              </span>
            ))}
          <StatusBadge status={row.answer_status} />
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${j.text}`}>
            품질 {row.eval_score != null ? `${row.eval_score}점` : '미평가'}
          </span>
          {row.latency_ms != null && (
            <span className="rounded-md bg-blue-50 px-2 py-0.5 font-mono text-[11px] text-sb-primary">
              {fmtMs(row.latency_ms)}
            </span>
          )}
          {row.llm_model && (
            <span className="rounded-md bg-violet-50 px-2 py-0.5 font-mono text-[11px] text-violet-700">
              {row.llm_model}
            </span>
          )}
        </div>

        {/* 질문 / 답변 / 근거 */}
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-sb-primary">질문</p>
            <p className="rounded-xl border border-blue-100 bg-blue-50/80 p-3 text-sm leading-relaxed text-gray-800">
              {row.question || '—'}
            </p>
          </div>
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-gray-400">AI 답변</p>
            <p className="whitespace-pre-wrap rounded-xl border border-gray-100 bg-white p-3 text-sm leading-relaxed text-gray-700">
              {row.answer || '(답변 없음)'}
            </p>
          </div>
          {row.refs?.length > 0 && (
            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-amber-700">근거·인용</p>
              <div className="space-y-2 rounded-xl border border-amber-100 bg-amber-50/80 p-3 text-xs">
                {row.refs.map((r, i) => (
                  <div key={i}>
                    <span className="font-semibold">{r.law}</span> {r.article}
                    {r.snippet && <p className="mt-1 line-clamp-3 text-[11px] text-amber-900/80">{r.snippet}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-right font-mono text-[11px] text-gray-400">{fmtTime(row.chat_time)}</p>
        </div>
      </Modal.Body>

      <Modal.Footer className="flex-wrap gap-2 border-0 px-6 pb-4 pt-0">
        {onDelete && (
          <Button variant="outline-danger" size="sm" className="me-auto" onClick={() => onDelete(row)}>
            <Trash2 size={14} className="me-1 inline" />
            삭제
          </Button>
        )}
        <Button variant="light" className="text-gray-600" onClick={onClose}>닫기</Button>
      </Modal.Footer>
    </Modal>
  );
}
